/**
 * Information towers (WORKBOARD §12, row I1; owner's rulings 1–2).
 *
 * A tower is a structure whose eye stands two person-heights above the ground it is built on.
 * It is humble: its reach is geometry, not a number. On a sphere the horizon from height h is
 * about √(2Rh), and the terrain raycast already lets a ridge extend a view and a hill cut it —
 * so a tower on high ground sees further with no rule for it. A tower watches its own planet
 * (person-resolution information is for layer 0; the system-scale picture is layer 2's fog).
 *
 * What it sees: living enemy units on its planet whose person eye it has a clear line to
 * (terrain by `hasLineOfSight`, props by `segmentCovered` — the same two checks a person's
 * sight uses). What it reports is a radar dot (ruling 26): a hostile position, no identity — a drone or a
 * person's eyes turn a dot into a known unit. Dots go into `state.sightings` (no unitId)
 * with the tick it was made, so consumers can age it (the information is perishable). A
 * tower's look that finds something replaces that tower's earlier dots; a look that finds nothing
 * leaves them ageing; a destroyed tower sees nothing and its last dots age out.
 *
 * Pure; `Match.stepAction` calls `updateSightings` every TOWER_SIGHT_PERIOD ticks while a
 * tower stands. Nothing here runs on the turn clock, and nothing changes when there is no tower.
 */

import type { GameState, PlayerState, StructureState, Sighting, Vec3 } from '../types';
import { eyePointAt, hasLineOfSight, losEyePoint } from './los';
import { segmentCovered } from './bullets';
import { personSetOf } from './scaleProfile';
import { structureJob } from './body';
import { roleLost } from './parts';
import type { AwarenessEvent } from './awareness';
import { personEyePoint, personLineOfSight } from './fineTerrain';
import { crownHides } from './foliage';

/** Ticks between tower looks (0.2 s at 120 Hz — the action bots' sight period). */
export const TOWER_SIGHT_PERIOD = 24;

/** X18: a standing structure whose job is the tower's sensor (radar dots on the sight beat). Partial destruction step 4: not once its radar head is gone (blind). */
export function isTowerSensor(s: Pick<StructureState, 'type' | 'health' | 'parts'>): boolean {
  return s.health > 0 && structureJob(s).sensor === 'tower' && !roleLost(s, 'function');
}

/** The tower's owning team (its owner unit's team), or null for an unowned tower. */
export function towerTeam(tower: StructureState, players: readonly PlayerState[]): number | null {
  return players.find(p => p.id === tower.ownerId)?.teamIndex ?? null;
}

/** The tower's eye: two person-heights above the ground it stands on. */
export function towerEyePoint(tower: StructureState, state: Pick<GameState, 'planets' | 'config'>): Vec3 {
  return eyePointAt(state.planets[tower.planetIndex], tower.surfaceDir, personSetOf(state).towerEyeHeight + (tower.baseLift ?? 0));   // (67.52: over its base)
}

/** Does this standing tower see this unit now? */
export function towerSees(tower: StructureState, unit: PlayerState, state: GameState): boolean {
  if (!isTowerSensor(tower)) return false;
  if (!unit.alive || unit.planetIndex !== tower.planetIndex) return false;
  const team = towerTeam(tower, state.players);
  if (team === null || unit.teamIndex === team) return false;
  const eye = towerEyePoint(tower, state);
  const ps = personSetOf(state);
  const target = personEyePoint(unit, state.planets, ps, ps.losEyeHeight);
  // the person-scale sampling step every other sight test uses (63.3 / 63.4 step 1: the 0.25 u default is ~4.5 people at
  // 1/20, so the tower's line could step past a trunk)
  return personLineOfSight(eye, target, state) && !segmentCovered(eye, target, state, undefined, ps.coverSampleStep)
    && !crownHides(state, tower.planetIndex, eye, target, unit.airborne ? undefined : unit.stance);   // 66.7: a crown deep enough hides (foliageSight)
}

/** Every sighting the standing towers make now, at `tick`. */
export function towerSightingsNow(state: GameState, tick: number): Sighting[] {
  const out: Sighting[] = [];
  for (const tower of state.structures) {
    if (!isTowerSensor(tower)) continue;
    const team = towerTeam(tower, state.players);
    if (team === null) continue;
    for (const u of state.players) {
      if ((u.landingLeft ?? 0) > 0 || u.boarded || !towerSees(tower, u, state)) continue;   // (69.3c: not yet landed)
      // a radar dot: where, not who (ruling 26)
      out.push({ teamIndex: team, planetIndex: u.planetIndex, surfaceDir: { ...u.surfaceDir }, pos: personEyePoint(u, state.planets, personSetOf(state), 0), tick, by: 'tower', sourceId: tower.id });
    }
  }
  // order by position, never by unit, so the list itself cannot say who is who
  return out.sort((a, b) => a.sourceId - b.sourceId || a.pos.x - b.pos.x || a.pos.y - b.pos.y || a.pos.z - b.pos.z);
}

/** The towers' look this tick, folded into the state's sightings. */
export function updateTowerSightings(state: GameState, tick: number): GameState {
  return mergeSightings(state, towerSightingsNow(state, tick));
}

/**
 * Fold fresh sightings into the state. Identified ones (drones): the newest per (team, unit) wins.
 * Radar dots (towers): a tower whose look found dots replaces its earlier dots. Everything else keeps its age.
 */
export function mergeSightings(state: GameState, fresh: Sighting[]): GameState {
  const prev = state.sightings ?? [];
  if (fresh.length === 0 && state.sightings !== undefined) return state;
  const refreshedTowers = new Set(fresh.filter(x => x.unitId === undefined).map(x => `${x.teamIndex}:${x.by}:${x.sourceId}`));
  const identified = new Map<string, Sighting>();
  const dots: Sighting[] = [];
  for (const x of prev) {
    if (x.unitId !== undefined) identified.set(`${x.teamIndex}:${x.unitId}`, x);
    else if (!refreshedTowers.has(`${x.teamIndex}:${x.by}:${x.sourceId}`)) dots.push(x);
  }
  for (const x of fresh) {
    if (x.unitId !== undefined) identified.set(`${x.teamIndex}:${x.unitId}`, x);
    else dots.push(x);
  }
  // stable order: team; identified units by id, then dots by source and position (deterministic, never by who)
  const sightings = [...identified.values(), ...dots].sort((a, b) => a.teamIndex - b.teamIndex
    || (a.unitId === undefined ? 1 : 0) - (b.unitId === undefined ? 1 : 0)
    || (a.unitId ?? 0) - (b.unitId ?? 0)
    || a.sourceId - b.sourceId || a.pos.x - b.pos.x || a.pos.y - b.pos.y || a.pos.z - b.pos.z);
  return { ...state, sightings };
}

/** Seconds since a sighting (perishable information: consumers fade it). */
export function sightingAge(s: Sighting, tick: number, dt: number): number {
  return Math.max(0, tick - s.tick) * dt;
}

/** A team's tower sightings as awareness events (source `seen`, by `sensor`), for the awareness core. */
/**
 * A team's sightings as awareness events: a drone's identified sighting is `seen` (by 'drone'); a tower's
 * radar dot is an anonymous `contact` (by 'sensor'), which an identified sighting at the same place absorbs.
 */
export function sightingAwarenessEvents(state: Pick<GameState, 'sightings'>, teamIndex: number, turn: number): AwarenessEvent[] {
  return (state.sightings ?? []).filter(s => s.teamIndex === teamIndex).map((s): AwarenessEvent =>
    s.unitId !== undefined && s.unitTeam !== undefined
      ? { kind: 'seen', unitId: s.unitId, teamIndex: s.unitTeam, planetIndex: s.planetIndex, surfaceDir: s.surfaceDir, pos: s.pos, turn: s.turn ?? turn, by: s.by === 'probe' ? 'probe' : 'drone' }
      : { kind: 'contact', planetIndex: s.planetIndex, surfaceDir: s.surfaceDir, pos: s.pos, turn, by: 'sensor' });
}
